import {
  ActionButton,
  Breadcrumbs,
  Card,
  PageHeader,
  Spinner,
} from '@campx-org/shared'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import {
  Alert,
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { EvaluatorEditorStore } from 'components/EvaluationEditor/EvaluatorEditor'
import { PdfViewerSimple } from 'components/PdfViewerSimple'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { BookletDetail, BreadcrumbLink } from './types'
import { flattenPartsToRows, getCellBg } from './utils'

const PANEL_HEIGHT = 'calc(100vh - 120px)'

const StyledTotalScoreSpan = styled('span')({
  color: '#4CAF50',
  fontSize: '15px',
  fontWeight: 'bold',
})

interface BookletDetailPageProps {
  breadcrumbs: BreadcrumbLink[]
  data: BookletDetail | null
  isLoading: boolean
  isError: boolean
  onRefetch: () => void
  headerActions?: ReactNode
}

/**
 * Shared renderer for the booklet detail view. Used by both external and
 * internal flows via their respective wrapper components.
 *
 * Layout: split-panel with a zoomable PDF viewer on the left and a details
 * panel on the right. The right panel contains:
 * - Evaluations summary card (barcode, subject, evaluator names, variance legend)
 * - Question-wise marks table with per-cell variance/admin-override coloring
 * - Remarks card (rendered only when remarks are present)
 *
 * The panel auto-hides when zoom exceeds 1.8× and restores when zoom drops back.
 * All breadcrumbs and data are injected by the wrapper — this component
 * contains no `examCategory` or flow-specific conditions.
 */
export default function BookletDetailPage({
  breadcrumbs,
  data,
  isLoading,
  isError,
  onRefetch,
  headerActions,
}: BookletDetailPageProps) {
  const [panelOpen, setPanelOpen] = useState(true)
  const [showEvaluatorDetails, setShowEvaluatorDetails] = useState(true)
  const hiddenByZoom = useRef(false)

  const { zoomLevel } = EvaluatorEditorStore.useState()
  const zoomPercent = Math.round(zoomLevel * 100)

  useEffect(() => {
    if (zoomLevel > 1.8) {
      if (panelOpen) {
        hiddenByZoom.current = true
        setPanelOpen(false)
      }
    } else {
      if (hiddenByZoom.current) {
        hiddenByZoom.current = false
        setPanelOpen(true)
      }
    }
  }, [zoomLevel])

  const handleZoomIn = () =>
    EvaluatorEditorStore.update((s) => {
      s.zoomLevel = Math.min(+(s.zoomLevel + 0.2).toFixed(1), 2.8)
    })

  const handleZoomOut = () =>
    EvaluatorEditorStore.update((s) => {
      s.zoomLevel = Math.max(+(s.zoomLevel - 0.2).toFixed(1), 0.5)
    })

  const bookletPdfUrl = data?.booklet?.bookletKeyUrl || ''
  const allEvaluators = data?.evaluators || []
  // Marks columns: exclude Admin & Scrutinizer (backend already excludes from evaluatorMarks)
  const evaluators = allEvaluators.filter(
    (e) => e.evaluatorType !== 'Admin' && e.evaluatorType !== 'Scrutinizer',
  )

  const hasMultipleEvaluators = evaluators.length > 1

  const allRows = flattenPartsToRows(data?.parts || [])
  const colCount = 2 + evaluators.length

  // Backend computes totalScore: use it directly (or fall back to '-' if not available)
  const totalScore = data?.totalScore ?? '-'

  return (
    <>
      <PageHeader
        title={<Breadcrumbs links={breadcrumbs} />}
        actions={headerActions}
      />

      {isError && (
        <Alert severity="error" onClose={onRefetch} sx={{ mx: 2, mt: 1 }}>
          Failed to load booklet details.
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <Spinner />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', height: PANEL_HEIGHT }}>
          {/* Left panel: PDF viewer */}
          <Box
            sx={{
              flex: panelOpen ? '3 1 0%' : '1 1 0%',
              transition: 'flex 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              borderRight: panelOpen ? '1px solid' : 'none',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ width: 36 }} />
              <Stack direction="row" alignItems="center" spacing={1}>
                <ActionButton
                  variant="outlined"
                  size="small"
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.5}
                >
                  <ZoomOutIcon fontSize="small" />
                </ActionButton>
                <Chip
                  label={`${zoomPercent}%`}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 60, justifyContent: 'center' }}
                />
                <ActionButton
                  variant="outlined"
                  size="small"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 2.8}
                >
                  <ZoomInIcon fontSize="small" />
                </ActionButton>
              </Stack>
              <ActionButton
                variant="outlined"
                size="small"
                onClick={() => setPanelOpen((v) => !v)}
                title={panelOpen ? 'Hide panel' : 'Show panel'}
              >
                {panelOpen ? (
                  <ChevronRightIcon fontSize="small" />
                ) : (
                  <ChevronLeftIcon fontSize="small" />
                )}
              </ActionButton>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'auto',
                bgcolor: '#f5f5f5',
              }}
            >
              <PdfViewerSimple pdfUrl={bookletPdfUrl} />
            </Box>
          </Box>

          {/* Right panel: info + marks */}
          <Box
            sx={{
              flex: panelOpen ? '2 1 0%' : '0 0 0px',
              minWidth: 0,
              transition: 'flex 0.3s ease',
              overflowY: panelOpen ? 'auto' : 'hidden',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              p: panelOpen ? 2 : 0,
              gap: 2,
            }}
          >
            {/* Evaluations summary card */}
            <Card
              title={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>All Evaluations</span>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setShowEvaluatorDetails(!showEvaluatorDetails)
                    }
                    sx={{ ml: 1 }}
                  >
                    {showEvaluatorDetails ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </Box>
              }
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Barcode
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ wordBreak: 'break-all' }}
                  >
                    {data?.booklet?.barcode || '-'}
                  </Typography>
                </Box>
                {data?.subject?.name && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Subject
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {data.subject.name}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {data?.booklet?.examType || '-'}
                  </Typography>
                </Box>
                {showEvaluatorDetails &&
                  allEvaluators.map((ev) => (
                    <Box key={`${ev.evaluatorType}-${ev.evaluatorId}`}>
                      <Typography variant="caption" color="text.secondary">
                        {ev.evaluatorType}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {ev.evaluatorName || '-'}
                      </Typography>
                    </Box>
                  ))}
              </Box>

              {hasMultipleEvaluators && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    {[
                      {
                        bg: '#E8F5E9',
                        border: '#388E3C',
                        label: 'No changes',
                      },
                      {
                        bg: '#FFFACD',
                        border: '#DAA520',
                        label: 'Variance',
                      },
                      {
                        bg: '#D1E7FD',
                        border: '#1976D2',
                        label: 'Admin modified',
                      },
                    ].map(({ bg, border, label }) => (
                      <Box
                        key={label}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            bgcolor: bg,
                            border: `1px solid ${border}`,
                            borderRadius: '2px',
                          }}
                        />
                        <Typography variant="caption">{label}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
            </Card>

            {/* Question-wise marks card */}
            {allRows.length > 0 && (
              <Card title="Question-wise Marks">
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Q.No</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Max</TableCell>
                        {evaluators.map((ev) => (
                          <TableCell
                            key={ev.evaluatorType}
                            align="center"
                            sx={{ fontWeight: 600 }}
                          >
                            {ev.evaluatorType}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allRows.map((row) => {
                        if (row.isPartHeader) {
                          return (
                            <TableRow key={row.key}>
                              <TableCell
                                colSpan={colCount}
                                sx={{
                                  bgcolor: 'grey.100',
                                  py: 0.75,
                                  borderBottom: 'none',
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <Typography variant="body2" fontWeight={700}>
                                    {row.partName}
                                  </Typography>
                                  {row.hasChoice && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      — Attempt any{' '}
                                      {row.numberOfQuestionsToAttempt} of{' '}
                                      {row.totalQuestionsInPart} questions
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          )
                        }
                        return (
                          <TableRow key={row.key} hover>
                            <TableCell>{row.qNo}</TableCell>
                            <TableCell>{row.max}</TableCell>
                            {evaluators.map((ev) => {
                              const val = row.evaluatorMarks?.[ev.evaluatorType]
                              const bg =
                                hasMultipleEvaluators ||
                                ev.evaluatorType === 'Admin'
                                  ? getCellBg(row, ev.evaluatorType)
                                  : 'transparent'
                              if (!val || val.marks == null) {
                                return (
                                  <TableCell
                                    key={ev.evaluatorType}
                                    align="center"
                                  >
                                    —
                                  </TableCell>
                                )
                              }
                              let display: string
                              if (val.isRevision) {
                                const from =
                                  val.oldMarks != null ? val.oldMarks : '-'
                                display = `${from} → ${val.marks}`
                              } else {
                                display = String(val.marks)
                              }
                              return (
                                <TableCell
                                  key={ev.evaluatorType}
                                  align="center"
                                >
                                  {bg !== 'transparent' ? (
                                    <Box sx={{ backgroundColor: bg, p: 0.5 }}>
                                      {display}
                                    </Box>
                                  ) : (
                                    display
                                  )}
                                </TableCell>
                              )
                            })}
                          </TableRow>
                        )
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {data?.booklet?.extMax ?? '-'}
                        </TableCell>
                        {evaluators.map((ev) => {
                          const total = ev.totalMarks
                          return (
                            <TableCell
                              key={ev.evaluatorType}
                              align="center"
                              sx={{ fontWeight: 700 }}
                            >
                              {total != null ? total : '—'}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    </TableFooter>
                  </Table>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    Final Score:{' '}
                    <StyledTotalScoreSpan>
                      {totalScore} / {data?.booklet?.extMax || '-'}
                    </StyledTotalScoreSpan>
                  </Typography>
                </Box>
              </Card>
            )}

            {/* Remarks — only rendered when data has them */}
            {(data?.remarks || []).length > 0 && (
              <Card title={`Remarks (${data?.remarks?.length || 0})`}>
                <Stack spacing={1.5}>
                  {(data?.remarks || []).map((r, i) => (
                    <Box key={`${r.createdAt}-${i}`}>
                      <Typography variant="caption" color="text.secondary">
                        {r.evaluatorType} • {r.evaluatorName}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {r.text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            )}
          </Box>
        </Box>
      )}
    </>
  )
}
